import React from 'react';

import { Label } from '../../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { Textarea } from '../../../ui/textarea';
import type { EvidenceStatus, EvidenceStatusValue } from '../../form/types';

/**
 * EvidenceStatusControl
 *
 * Compact, reusable control for reviewing a single evidence item.
 * Lets a user select a status and optionally add brief context notes.
 * Notes are toggleable and auto-open when existing notes are present.
 */
interface EvidenceStatusControlProps {
  /** Unique id prefix for input elements and ARIA labelling */
  id: string;
  /** Human-readable field label (also used for ARIA labels) */
  label: string;
  /** Current value (status + notes) */
  value: EvidenceStatus;
  /** Change handler; called whenever status or notes change */
  onChange: (value: EvidenceStatus) => void;
  /** Optional container className */
  className?: string;
  /** If true, hides the visible field label (screen readers keep context) */
  hideLabel?: boolean;
  /** Notes visibility rule. Currently always treated as optional/toggle-only */
  notesVisibility?: 'auto' | 'optional';
  /** Placeholder hint shown in the notes textarea */
  contextPlaceholder?: string;
  /** Render mode: "full" (status + notes) or "notes-only" */
  mode?: 'full' | 'notes-only';
  /** Locks the status field when true; shows a read-only chip */
  disabled?: boolean;
}

const STATUS_OPTIONS: Array<{ value: EvidenceStatusValue; label: string }> = [
  { value: 'EXISTS_NOT_HAVE', label: 'Exists (needs follow-up)' },
  { value: 'DOES_NOT_EXIST', label: 'Does not exist' },
  { value: 'UNCERTAIN', label: 'Uncertain' },
];

/**
 * Renders the status selector and optional notes for a single evidence row.
 */
const EvidenceStatusControl: React.FC<EvidenceStatusControlProps> = ({
  id,
  label,
  value,
  onChange,
  className = '',
  hideLabel = false,
  notesVisibility = 'auto',
  contextPlaceholder,
  mode = 'full',
  disabled = false,
}) => {
  const isNotesOnly = mode === 'notes-only';
  const [notesOpen, setNotesOpen] = React.useState<boolean>(() =>
    Boolean(value.notes?.trim()),
  );
  const showNotes = !disabled && notesOpen;

  // Track whether the user has manually toggled the notes panel to avoid
  // overriding their choice with auto-open behavior.
  const userToggledRef = React.useRef<boolean>(false);

  // Standardize auto-open behavior: when a row is not confirmed and has notes,
  // ensure the notes area is opened even if notes arrive after mount (e.g.,
  // coverage sub-rows seeded via effect). Close when row becomes confirmed.
  React.useEffect(() => {
    const hasNotes = Boolean(value.notes?.trim());
    if (disabled || value.status === 'CONFIRMED') {
      if (notesOpen) setNotesOpen(false);
      // Reset user intent when the control becomes locked
      userToggledRef.current = false;
      return;
    }
    if (hasNotes && !notesOpen && !userToggledRef.current) {
      setNotesOpen(true);
    }
  }, [disabled, value.status, value.notes, notesOpen]);

  const handleStatusChange = (newStatus: EvidenceStatusValue) => {
    onChange({ ...value, status: newStatus });
  };

  const handleNotesChange = (notes: string) => {
    onChange({ ...value, notes });
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {!hideLabel && (
        <Label htmlFor={`${id}-status`} className="text-sm font-medium">
          {label}
        </Label>
      )}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          {!isNotesOnly && !disabled && (
            <Select value={value.status} onValueChange={handleStatusChange}>
              <SelectTrigger
                id={`${id}-status`}
                tabIndex={0}
                className="h-8 px-3 text-sm rounded-md border-gray-200 shadow-none focus:ring-0 focus:border-gray-300"
                aria-label={`${label} status`}
              >
                <SelectValue placeholder="Set status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    className="text-sm"
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!isNotesOnly && disabled && (
            <div
              id={`${id}-status`}
              aria-label={`${label} status (locked)`}
              aria-disabled
              className="h-8 px-3 text-sm rounded-md border border-gray-200 bg-gray-100 text-gray-800 flex items-center"
            >
              {(() => {
                const current = STATUS_OPTIONS.find(
                  (o) => o.value === value.status,
                );
                return current ? current.label : 'Confirmed';
              })()}
            </div>
          )}
        </div>

        {notesVisibility === 'optional' &&
          (!disabled ? (
            <button
              type="button"
              onClick={() => {
                userToggledRef.current = true;
                setNotesOpen((v) => !v);
              }}
              className="h-7 w-7 inline-flex items-center justify-center rounded-md text-gray-900 hover:bg-gray-100 focus:outline-none"
              aria-label={`${showNotes ? 'Hide context' : 'Add context'} for ${label}`}
              title={showNotes ? 'Hide context' : 'Add context'}
            >
              {showNotes ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path stroke="currentColor" strokeWidth="1.5" d="M4 10h12" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                >
                  <path
                    stroke="currentColor"
                    strokeWidth="1.5"
                    d="M10 4v12M4 10h12"
                  />
                </svg>
              )}
            </button>
          ) : (
            <div className="h-7 w-7" aria-hidden />
          ))}
      </div>

      {showNotes && (
        <div className="mt-1">
          <div className="text-[11px] font-medium text-gray-700 mb-0.5">
            Context - Visible to attorneys and internal staff.
          </div>
          <Textarea
            id={`${id}-notes`}
            value={value.notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder={contextPlaceholder ?? 'Add context…'}
            rows={2}
            aria-label={`${label} notes`}
            className="text-sm"
          />
        </div>
      )}
    </div>
  );
};

export default EvidenceStatusControl;
